import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
	fetchTachesAutre,
	fetchTypesTacheVehicule,
} from "../../../store/actions/autreActions"
import { PROPRE_VEHICULE_TYPE_CHOICES } from "../../../utilities/Addons"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import Loader from "../../basics/Loader"
import TacheModalV2 from "./TacheModalV2"
import TacheVehiculeModalV2 from "./TacheVehiculeModalV2"
import AssignationV2 from "./AssignationV2"

const TYPE_LABELS = {}
PROPRE_VEHICULE_TYPE_CHOICES.forEach(([v, l]) => {
	TYPE_LABELS[v] = l
})

// Résumé lisible de la portée d'une tâche de base (catalogue véhicule).
const porteeLabel = (t) => {
	if (t.portee === 1)
		return (
			"Type : " +
			(t.portee_types || []).map((v) => TYPE_LABELS[v] || v).join(", ")
		)
	if (t.portee === 2)
		return `Véhicules (${(t.portee_vehicules || []).length})`
	return "Flotte (tous)"
}

const styles = {
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 16,
	},
	title: { fontSize: 15, fontWeight: 600, color: COLORS.text },
	count: { fontSize: 13, color: COLORS.textLight },
	td: { ...tableStyles.td, whiteSpace: "normal" },
	empty: {
		...tableStyles.empty,
		padding: "40px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
}

const Row = ({ onOpen, item, index, cells }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	return (
		<tr
			onDoubleClick={() => onOpen(item)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			{cells.map((c, i) => (
				<td key={i} style={{ ...styles.td, ...(c.style || {}) }}>
					{c.value}
				</td>
			))}
		</tr>
	)
}

const Table = ({ loading, items, columns, renderCells, onOpen, emptyLabel }) => (
	<div style={tableStyles.container}>
		{loading ? (
			<Loader variant="ring" message="Chargement…" />
		) : items.length === 0 ? (
			<div style={styles.empty}>{emptyLabel}</div>
		) : (
			<table style={tableStyles.table}>
				<thead>
					<tr>
						{columns.map((c) => (
							<th key={c} style={tableStyles.th}>
								{c}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{items.map((item, index) => (
						<Row
							key={item.id}
							item={item}
							index={index}
							onOpen={onOpen}
							cells={renderCells(item)}
						/>
					))}
				</tbody>
			</table>
		)}
	</div>
)

// Onglet "Tâches" de la Configuration, scindé en deux sections :
//  - "Tâches de base" : catalogue véhicule (TypeTacheVehicule + portée).
//  - "Nouvelles tâches" : tâches "autre" (TacheAutre, texte libre).
const TachesV2 = () => {
	const dispatch = useDispatch()
	const [section, setSection] = useState("vehicules")
	const [editing, setEditing] = useState(null) // tâche en édition | null

	const typesTV = useSelector((s) => s.autreInfo.typesTacheVehicule)
	const fetchTV = useSelector((s) => s.autreInfo.fetchTypesTV)
	const taches = useSelector((s) => s.autreInfo.taches)
	const fetchAutre = useSelector((s) => s.autreInfo.fetchTaches)

	useEffect(() => {
		dispatch(fetchTypesTacheVehicule())
		dispatch(fetchTachesAutre())
	}, [dispatch])

	const closeModal = () => setEditing(null)

	const baseSorted = [...(typesTV || [])].sort(
		(a, b) => (a.ordre || 0) - (b.ordre || 0),
	)
	const autreSorted = [...(taches || [])].sort((a, b) =>
		(a.texte || "").localeCompare(b.texte || "", "fr"),
	)

	const isVeh = section === "vehicules"
	const isAssign = section === "assignation"
	const items = isVeh ? baseSorted : autreSorted
	const loading = isVeh
		? fetchTV.loading && baseSorted.length === 0
		: fetchAutre.loading && autreSorted.length === 0

	return (
		<>
			{editing &&
				(editing._kind === "vehicule" ? (
					<TacheVehiculeModalV2
						tache={editing}
						onClose={closeModal}
					/>
				) : (
					<TacheModalV2 tache={editing} onClose={closeModal} />
				))}

			<div style={gs.sectionTabs}>
				<button
					style={gs.sectionTab(section === "vehicules")}
					onClick={() => setSection("vehicules")}
				>
					Véhicules
				</button>
				<button
					style={gs.sectionTab(section === "base")}
					onClick={() => setSection("base")}
				>
					Base
				</button>
				<button
					style={gs.sectionTab(section === "assignation")}
					onClick={() => setSection("assignation")}
				>
					Assignation
				</button>
			</div>

			{isAssign ? (
				<AssignationV2 />
			) : (
				<>
					<div style={styles.header}>
						<div
							style={{
								display: "flex",
								alignItems: "baseline",
								gap: 10,
							}}
						>
							<span style={styles.title}>
								{isVeh ? "Tâches véhicule" : "Tâches de base"}
							</span>
							<span style={styles.count}>{items.length}</span>
						</div>
						<button
							style={gs.btnPrimary}
							onClick={() =>
								setEditing(
									isVeh
										? { _kind: "vehicule" }
										: { _kind: "base" },
								)
							}
						>
							{isVeh
								? "+ Nouvelle tâche véhicule"
								: "+ Nouvelle tâche"}
						</button>
					</div>

					{isVeh ? (
						<Table
							loading={loading}
							items={baseSorted}
							columns={["Nom", "Portée"]}
							emptyLabel="Aucune tâche véhicule."
							onOpen={(t) =>
								setEditing({ ...t, _kind: "vehicule" })
							}
							renderCells={(t) => [
								{
									value: t.nom || "—",
									style: { fontWeight: 500 },
								},
								{
									value: porteeLabel(t),
									style: { color: COLORS.textMuted },
								},
							]}
						/>
					) : (
						<Table
							loading={loading}
							items={autreSorted}
							columns={["Texte"]}
							emptyLabel="Aucune tâche."
							onOpen={(t) => setEditing({ ...t, _kind: "base" })}
							renderCells={(t) => [
								{
									value: t.texte || "—",
									style: { fontWeight: 500 },
								},
							]}
						/>
					)}
				</>
			)}
		</>
	)
}

export default TachesV2
